import useRevokeCredential from '@hooks/mutations/credential/useRevokeCredential';
import useRevokeCredentialAndCancelBilling from '@hooks/mutations/credential/useRevokeCredentialAndCancelBilling';
import useMutate from '@hooks/mutations/useMutate';
import useCredentialBillings from '@hooks/queries/credential/billing/useCredentialBillings';
import useUserService from '@hooks/queries/credential/onboarding/useUserService';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { ButtonProps } from '@mui/material';
import { Credential } from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import { useParties } from '../../contexts/PartiesContext';
import TemplateContract from '../../utils/TemplateContract';
import {
  ERR_REV_CAN_CRED,
  ERR_REV_CRED,
  SUC_REV_CAN_CRED,
  SUC_REV_CRED,
} from '../../utils/strings';

export const useCreateCredentialActions = (
  onClose: () => void,
  r?: TemplateContract<Credential>,
) => {
  const { party } = useParties();
  const billings = useCredentialBillings();
  const userService = useUserService();
  const { showBoundary } = useErrorBoundary<Error>();
  const { enqueueSnackbar } = useSnackbar();
  const mutate = useMutate();
  const revokeCredential = useRevokeCredential();

  const revokeAndCancelBilling = useRevokeCredentialAndCancelBilling();

  const findCredentialBillingById = useCallback(
    (credentialId: string) => {
      return billings.data?.find((b) => b.payload.credentialId === credentialId);
    },
    [billings.data],
  );
  const revoke = async () => {
    if (!userService.data || !r) {
      throw new Error('No user service');
    }
    const payload = {
      userServiceCid: userService.data.contractId,
      credentialCid: r.contractId,
    };
    await mutate(revokeCredential, payload, SUC_REV_CRED, ERR_REV_CRED);
    onClose();
  };

  const revokeAndCancel = useCallback(async () => {
    if (!userService.data || !r) {
      showBoundary(new Error(ERR_REV_CRED, { cause: 'User service is not available.' }));
      throw new Error(ERR_REV_CRED);
    }
    const billing = findCredentialBillingById(r.payload.id);
    if (!billing) {
      enqueueSnackbar(`${ERR_REV_CRED}: 'Billing could not be found.`, {
        variant: 'error',
      });
      throw new Error(ERR_REV_CRED);
    }
    const payload = {
      userServiceCid: userService.data.contractId,
      credentialCid: r.contractId,
      credentialBillingCid: billing.contractId,
    };
    await mutate(revokeAndCancelBilling, payload, SUC_REV_CAN_CRED, ERR_REV_CAN_CRED);
    onClose();
  }, [
    userService.data,
    findCredentialBillingById,
    revokeAndCancelBilling,
    enqueueSnackbar,
    mutate,
    showBoundary,
    onClose,
    r,
  ]);

  if (!r) {
    return [];
  }

  const billing = findCredentialBillingById(r.payload.id);
  if (billing) {
    return [
      { color: 'error' as ButtonProps['color'], name: 'Revoke', action: revoke },
      {
        color: 'error' as ButtonProps['color'],
        name: 'Revoke And Cancel',
        action: revokeAndCancel,
      },
    ];
  }
  const isIssuer = r.payload.issuer === party;
  const isHolder = r.payload.holder === party;
  if (isIssuer || isHolder) {
    return [{ color: 'error' as ButtonProps['color'], name: 'Revoke', action: revoke }];
  }
  return [];
};
